import * as React from 'react';
import { AUTOMATION_ACTION_TYPE } from '../../../../MedicareProAPI/Entities/Automation/AutomationAction';
import { Utilities } from '../../../../MedicareProAPI/Utilities/Utilities';
import { WebAPI } from '../../../../MedicareProAPI/API/WebAPI';
import { UserLetterRepository } from '../../../../MedicareProAPI/Repositories/User/UserLetterRepository';
import { UserVoipPhoneNumberRepository } from '../../../../MedicareProAPI/Repositories/User/UserVoipPhoneNumberRepository';
import { UserVoipExtensionRepository } from '../../../../MedicareProAPI/Repositories/User/UserVoipExtensionRepository';
import type { UserIntegrationProvider } from '../../../../MedicareProAPI/Entities/User/UserIntegrationProvider';
import type { App } from '../../../../MedicareProAPI/Entities/App';
import { AppRepository } from '../../../../MedicareProAPI/Repositories/AppRepository';
import { UserIntegrationProviderRepository } from '../../../../MedicareProAPI/Repositories/User/UserIntegrationProviderRepository';
import { UserLetterTemplateSelect } from './UserLetterTemplateSelect';
import type { ThanksIOTemplateProps } from './ThanksIOTemplate';
import { ThanksIOTemplateSelect } from './ThanksIOTemplate';
import type { DirectMailManagerTemplateProps } from './DirectMailManagerTemplate';
import { DirectMailManagerTemplateSelect } from './DirectMailManagerTemplate';
import { INTEGRATION_TYPE } from '../../../Constants/Integrations';

export interface UserServiceSelectProps {
  type: AUTOMATION_ACTION_TYPE;
  userAccessToken: string;
  userId: number;

  extensions?: boolean;
  phoneNumbers?: boolean;
  templates?: boolean;

  userVoIPExtensionId?: number;
  userVoIPPhoneNumberId?: number;
  userLetterTemplateId?: number;

  userContactSyncListId?: any;
  userContactSyncExtensionId?: number;
  userContactSyncAccountId?: number;
  userContactSyncGroupId?: number;

  userAudioFileId?: any;

  thanksioTemplate: ThanksIOTemplateProps;
  directMailManagerTemplate: DirectMailManagerTemplateProps;
  appId?: number;
  userIntegrationProviderId?: number;

  onUserVoIPExtensionIdChange?: (userVoIPExtensionId: number) => void;
  onUserVoIPPhoneNumberIdChange?: (userVoIPPhoneNumberId: number) => void;
  onUserLetterTemplateIdChange?: (userLetterTemplateId: number) => void;

  onUserContactSyncListIdChange?: (userContactSyncListId: any) => void;
  onUserContactSyncExtensionIdChange?: (userContactSyncExtensionId: number) => void;
  onUserContactSyncAccountIdChange?: (userContactSyncAccountId: number) => void;
  onUserContactSyncGroupIdChange?: (userContactSyncGroupId: any) => void;

  onUserAudioFileIdChange?: (userContactSyncGroupId: any) => void;

  onThanksioTemplateChange?: (thanksioTemplateData: ThanksIOTemplateProps) => void;
  onDirectMailManagerTemplateChange?: (
    directMailManagerTemplateData: DirectMailManagerTemplateProps
  ) => void;

  onServiceChange?: (service: App | UserIntegrationProvider) => void;
  onSetupIntegrationService?: (type: INTEGRATION_TYPE) => void;
  onSetupTemplate?: () => void;
  refreshKey?: number;
}

export const UserServiceSelect = (props: UserServiceSelectProps): React.JSX.Element => {
  const [type, setType] = React.useState<AUTOMATION_ACTION_TYPE>();

  // TODO: For scalability, turn this into a data object with it own type
  const [userIntegrationProviderId, setUserIntegrationProviderId] = React.useState<number>(
    props.userIntegrationProviderId
  );
  const [userIntegrationProviderReference, setUserIntegrationProviderReference] =
    React.useState<string>();
  const [userVoIPPhoneNumberId, setUserVoIPPhoneNumberId] = React.useState<number>(
    props.userVoIPPhoneNumberId
  );
  const [userVoIPExtensionId, setUserVoIPExtensionId] = React.useState<number>(
    props.userVoIPExtensionId
  );

  const [userAudioFileId, setUserAudioFileId] = React.useState<number>(props.userAudioFileId);

  const [userLetterTemplateId, setUserLetterTemplateId] = React.useState<number>(
    props.userLetterTemplateId
  );
  const [userContactSyncListId, setUserContactSyncListId] = React.useState<any>(
    props.userContactSyncListId
  );
  const [userContactSyncGroupId, setUserContactSyncGroupId] = React.useState<any>(
    props.userContactSyncGroupId
  );
  const [userContactSyncExtensionId, setUserContactSyncExtensionId] = React.useState<number>(
    props.userContactSyncExtensionId
  );
  const [userContactSyncAccountId, setUserContactSyncAccountId] = React.useState<number>(
    props.userContactSyncAccountId
  );

  const [thanksioTemplate, setThanksioTemplate] = React.useState<ThanksIOTemplateProps>(
    props.thanksioTemplate
  );
  const [directMailManagerTemplate, setDirectMailManagerTemplate] =
    React.useState<ThanksIOTemplateProps>(props.directMailManagerTemplate);

  const [appId, setAppId] = React.useState<number>(props.appId);

  // TODO: Make these redundant
  const [userServices, setUserServices] = React.useState<any>([]);
  const [userServicesLoading, setUserServicesLoading] = React.useState<boolean>(false);

  const [userActionServices, setUserActionServices] = React.useState<any>([]);
  const [userActionServicesLoading, setUserActionServicesLoading] = React.useState<boolean>(false);

  const [userVoIPExtensions, setUserVoIPExtensions] = React.useState<any>([]);
  const [userVoIPExtensionsLoading, setUserVoIPExtensionsLoading] = React.useState<boolean>(false);

  const [userContactSyncLists, setUserContactSyncLists] = React.useState<any>([]);
  const [userContactSyncListsLoading, setUserContactSyncListsLoading] =
    React.useState<boolean>(false);

  const [userAudioFilesLists, setUserAudioFilesLists] = React.useState<any>([]);
  const [userAudioFilesListsLoading, setUserAudioFilesListsLoading] =
    React.useState<boolean>(false);

  const [userContactSyncGroupLists, setUserContactSyncGroupLists] = React.useState<any>([]);
  const [userContactSyncGroupsLoading, setUserContactSyncGroupsLoading] =
    React.useState<boolean>(false);

  const [userVoIPPhoneNumbers, setUserVoIPPhoneNumbers] = React.useState<any>([]);
  const [userVoIPPhoneNumbersLoading, setUserVoIPPhoneNumbersLoading] =
    React.useState<boolean>(false);

  const [currentUserService, setCurrentUserService] = React.useState<any>();

  const webAPI = new WebAPI();
  webAPI.setAccessToken(props.userAccessToken);

  const userLetterRepository = new UserLetterRepository();
  userLetterRepository.setAccessToken(props.userAccessToken);

  const userVoIPExtensionRepository = new UserVoipExtensionRepository();
  userVoIPExtensionRepository.setAccessToken(props.userAccessToken);

  const userVoIPPhoneNumberRepository = new UserVoipPhoneNumberRepository();
  userVoIPPhoneNumberRepository.setAccessToken(props.userAccessToken);

  const appRepository = new AppRepository();
  appRepository.setAccessToken(props.userAccessToken);

  const userIntegrationProviderRepository = new UserIntegrationProviderRepository();
  userIntegrationProviderRepository.setAccessToken(props.userAccessToken);

  const servicesUsingListIdAsContactSyncList = [
    'mailboxpower',
    'directmailmanager',
    'medizues',
    'aweber',
    'thanksio',
    'mailchimp',
    'constantcontact',
  ];
  const servicesUsingExtensionIdAsContactSyncList = ['phonecom'];
  const servicesUsingAccountIdAsContactSyncList = ['dialpad', 'openphone'];

  const servicesContactSyncListIdNotInteger = ['directmailmanager'];

  React.useEffect(() => {
    setType(props.type);
  }, [props.type]);

  React.useEffect(() => {
    if (type) {
      if (
        type === AUTOMATION_ACTION_TYPE.SEND_EMAIL ||
        type === AUTOMATION_ACTION_TYPE.SEND_MAIL ||
        type === AUTOMATION_ACTION_TYPE.SEND_SMS ||
        type === AUTOMATION_ACTION_TYPE.SYNC_CONTACT ||
        type === AUTOMATION_ACTION_TYPE.SYNC_REMOVE_CONTACT ||
        type === AUTOMATION_ACTION_TYPE.SEND_RINGLESS_VM
      ) {
        let sendType =
          type.substring(5) == 'contact' || type.substring(12) == 'contact'
            ? 'contact-sync'
            : type.substring(5);

        sendType == 'ringless_vm' ? (sendType = 'ringless-vm') : '';

        setUserActionServicesLoading(true);
        setUserServicesLoading(true);

        webAPI
          .getUserIntegrationFeatureAvailability(props.userId, sendType)
          .then((integrations) => {
            setUserServices(integrations);
            setUserServicesLoading(false);

            const newList = Array.from(integrations, (item: any): any => {
              return {
                value: parseInt(item.id),
                label: item.name,
                entityType: item.entityType,
                reference: item.reference,
              };
            });
            console.log(newList);
            if (type === AUTOMATION_ACTION_TYPE.SEND_EMAIL) {
              newList.unshift({ value: '', label: 'Default' });
            }

            setUserActionServices(newList);
            setUserActionServicesLoading(false);
          });
      }
    }
  }, [type, props.refreshKey]);

  React.useEffect(() => {
    if (userServices && (appId || userIntegrationProviderId)) {
      let type;
      let serviceId;

      if (userIntegrationProviderId) {
        type = 'oauth';
        serviceId = userIntegrationProviderId;
      } else if (appId) {
        type = 'pat';
        serviceId = appId;
      }

      if (type && serviceId) {
        const newCurrentUserService = getUserService(serviceId, type);

        setCurrentUserService(newCurrentUserService);

        if (newCurrentUserService) {
          setUserIntegrationProviderReference(newCurrentUserService.reference);
        }
      }
    } else {
      setCurrentUserService(null);
    }
  }, [appId, userIntegrationProviderId, userServices]);

  React.useEffect(() => {
    if (
      (userIntegrationProviderId || appId) &&
      type === AUTOMATION_ACTION_TYPE.SEND_SMS &&
      props.extensions === true
    ) {
      console.log('getting phone data');
      setUserVoIPExtensionsLoading(true);
      setUserVoIPPhoneNumbersLoading(true);

      if (userIntegrationProviderId) {
        userVoIPExtensionRepository
          .getUserVoipExtensions(props.userId, userIntegrationProviderId)
          .then((userVoIPExtensionsResponse) => {
            setUserVoIPExtensionsLoading(false);

            const newList = Array.from(userVoIPExtensionsResponse.getItems(), (item) => {
              return { value: item.getExtensionId(), label: item.getExtensionNumber() };
            });

            setUserVoIPExtensions(newList);
          });

        userVoIPPhoneNumberRepository
          .getUserVoipPhoneNumbers(props.userId, userIntegrationProviderId)
          .then((userVoIPPhoneNumbersResponse) => {
            setUserVoIPPhoneNumbersLoading(false);

            const newList = Array.from(userVoIPPhoneNumbersResponse.getItems(), (item) => {
              return {
                value: item.getPhoneNumberId(),
                label: Utilities.phoneNumberDisplay(item.getPhoneNumber()),
              };
            });

            setUserVoIPPhoneNumbers(newList);
          });
      }

      if (appId) {
        setUserVoIPExtensionsLoading(false);
        userVoIPPhoneNumberRepository
          .getUserVoipPhoneNumbersApp(props.userId, appId)
          .then((userVoIPPhoneNumbersResponse) => {
            setUserVoIPPhoneNumbersLoading(false);

            const newList = Array.from(userVoIPPhoneNumbersResponse.getItems(), (item) => {
              return {
                value: item.getPhoneNumberId() || item.getId(),
                label: Utilities.phoneNumberDisplay(item.getPhoneNumber()),
              };
            });
            console.log(newList);
            setUserVoIPPhoneNumbers(newList);
          });
      }
    }

    if (
      (userIntegrationProviderId || appId) &&
      (type === AUTOMATION_ACTION_TYPE.SYNC_CONTACT ||
        type === AUTOMATION_ACTION_TYPE.SYNC_REMOVE_CONTACT)
    ) {
      setUserContactSyncListsLoading(true);

      if (userIntegrationProviderReference) {
        if (servicesUsingExtensionIdAsContactSyncList.includes(userIntegrationProviderReference)) {
          // Handle contact sync vendors where phone extension is needed
          userVoIPExtensionRepository
            .getUserVoipExtensions(props.userId, userIntegrationProviderId)
            .then((userVoIPExtensionsResponse) => {
              setUserContactSyncListsLoading(false);

              const newList = Array.from(userVoIPExtensionsResponse.getItems(), (item) => {
                return { value: item.getExtensionId(), label: item.getExtensionNumber() };
              });

              setUserContactSyncLists(newList);

              getUserVoipGroups(userContactSyncExtensionId);
            });
        } else if (
          servicesUsingAccountIdAsContactSyncList.includes(userIntegrationProviderReference)
        ) {
          // Handle contact sync vendors where we sync to an accountId
          setUserContactSyncListsLoading(false);

          const newList = [
            { value: currentUserService.account_id, label: currentUserService.account_id },
          ];

          setUserContactSyncLists(newList);
        } else {
          // Handle contact sync vendors where we sync to a list
          webAPI
            .getUserIntegrationContactSyncLists(userIntegrationProviderReference)
            .then((userIntegrationContactSyncListsResponse) => {
              setUserContactSyncListsLoading(false);

              const newList = Array.from(
                userIntegrationContactSyncListsResponse,
                (item: any): any => {
                  return { value: item.id, label: item.label };
                }
              );

              setUserContactSyncLists(newList);
            });
        }
      }
    }

    if ((userIntegrationProviderId || appId) && type === AUTOMATION_ACTION_TYPE.SEND_RINGLESS_VM) {
      setUserAudioFilesListsLoading(true);
      webAPI
        .getUserIntegrationSlyBroadcastAudioFiles(props.userId)
        .then((userIntegrationRinglessVmAudioFilesResponse) => {
          const newList = [];
          userIntegrationRinglessVmAudioFilesResponse.forEach((item) => {
            newList.push({ value: item, label: item });
          });

          setUserAudioFilesLists(newList);
          setUserAudioFilesListsLoading(false);
        });
    }
  }, [type, userIntegrationProviderId, appId, userIntegrationProviderReference]);

  const getUserServiceByReference = (userServiceReference: string): any => {
    // TODO: 'id' should be an int
    const serviceData = userServices.find(
      (userService) => userService.reference === userServiceReference
    );

    return serviceData;
  };

  const getUserService = (userServiceId: string, type: string): any => {
    // TODO: 'id' should be an int
    const serviceData = userServices.find(
      (userService) =>
        parseInt(userService.id) === parseInt(userServiceId) && userService.entityType === type
    );

    return serviceData;
  };

  const userServiceChange = (event: React.SyntheticEvent) => {
    const inputElement = event.target as HTMLFormElement;
    const inputValue = inputElement.value;

    const serviceData = getUserServiceByReference(inputValue);

    setUserIntegrationProviderReference(inputValue);

    if (serviceData && serviceData.entityType === 'oauth') {
      setAppId(null);
      setUserIntegrationProviderId(serviceData.id);

      if (props.onServiceChange) {
        userIntegrationProviderRepository
          .getUserIntegrationProvider(serviceData.id)
          .then((userIntegrationProvider) => {
            props.onServiceChange(userIntegrationProvider);
          });
      }
    } else if (serviceData && serviceData.entityType === 'pat') {
      setUserIntegrationProviderId(null);
      setAppId(serviceData.id);

      if (props.onServiceChange) {
        appRepository.getApp(serviceData.id).then((app) => {
          props.onServiceChange(app);
        });
      }
    } else {
      setUserIntegrationProviderId(null);
      setAppId(null);

      props.onServiceChange(null);
    }

    setCurrentUserService(serviceData);
  };

  const userVoIPExtensionChange = (event: React.SyntheticEvent) => {
    const inputElement = event.target as HTMLFormElement;
    const inputValue = parseInt(inputElement.value);

    if (props.onUserVoIPExtensionIdChange) {
      props.onUserVoIPExtensionIdChange(inputValue);
    }

    setUserVoIPExtensionId(inputValue);
  };

  const userAudioFileChange = (event: React.SyntheticEvent) => {
    const inputElement = event.target as HTMLFormElement;
    const inputValue = inputElement.value;

    if (props.onUserAudioFileIdChange) {
      props.onUserAudioFileIdChange(inputValue);
    }

    setUserAudioFileId(inputValue);
  };

  const getUserVoipGroups = (extensionId) => {
    setUserContactSyncGroupsLoading(true);
    webAPI
      .getUserIntegrationOAuthVoipGroupsByExtension(
        props.userId,
        userIntegrationProviderReference,
        currentUserService.account_id,
        extensionId
      )
      .then((response) => {
        setUserContactSyncGroupLists(response);
        setUserContactSyncGroupsLoading(false);
      });
  };

  const userVoIPPhoneNumberChange = (event: React.SyntheticEvent) => {
    const inputElement = event.target as HTMLFormElement;
    const inputValue = parseInt(inputElement.value);

    if (props.onUserVoIPPhoneNumberIdChange) {
      props.onUserVoIPPhoneNumberIdChange(inputValue);
    }

    setUserVoIPPhoneNumberId(inputValue);
  };

  const userContactSyncListIdChange = (event: React.SyntheticEvent) => {
    const inputElement = event.target as HTMLFormElement;
    const inputValue = inputElement.value;

    if (props.onUserContactSyncListIdChange) {
      props.onUserContactSyncListIdChange(inputValue);
    }
  };

  const userContactSyncGroupIdChange = (event: React.SyntheticEvent) => {
    const inputElement = event.target as HTMLFormElement;
    const inputValue = inputElement.value;

    if (props.onUserContactSyncGroupIdChange) {
      props.onUserContactSyncGroupIdChange(parseInt(inputValue));
    }
  };

  const userContactSyncExtensionIdChange = (event: React.SyntheticEvent) => {
    const inputElement = event.target as HTMLFormElement;
    const inputValue = parseInt(inputElement.value);

    if (props.onUserContactSyncExtensionIdChange) {
      props.onUserContactSyncExtensionIdChange(inputValue);

      if (userIntegrationProviderReference == 'phonecom') {
        getUserVoipGroups(inputValue);
      }
    }
  };

  const userContactSyncAccountIdChange = (event: React.SyntheticEvent) => {
    const inputElement = event.target as HTMLFormElement;
    const inputValue = parseInt(inputElement.value);

    if (props.onUserContactSyncAccountIdChange) {
      props.onUserContactSyncAccountIdChange(inputValue);
    }

    setUserContactSyncAccountId(inputValue);
  };

  const handleSetupIntegration = (type: AUTOMATION_ACTION_TYPE) => {
    // TODO: make a map for this
    let newType;
    if (type === AUTOMATION_ACTION_TYPE.SEND_EMAIL) {
      newType = INTEGRATION_TYPE.EMAIL;
    }
    if (type === AUTOMATION_ACTION_TYPE.SEND_SMS) {
      newType = INTEGRATION_TYPE.VOIP_SMS;
    }
    if (type === AUTOMATION_ACTION_TYPE.SEND_MAIL) {
      newType = INTEGRATION_TYPE.MAIL;
    }
    if (type === AUTOMATION_ACTION_TYPE.SYNC_CONTACT) {
      newType = INTEGRATION_TYPE.SYNC;
    }
    if (
      type === AUTOMATION_ACTION_TYPE.SYNC_CONTACT ||
      type === AUTOMATION_ACTION_TYPE.SYNC_REMOVE_CONTACT
    ) {
      newType = INTEGRATION_TYPE.SYNC;
    }
    if (type === AUTOMATION_ACTION_TYPE.SEND_RINGLESS_VM) {
      newType = INTEGRATION_TYPE.RINGLESS_VM;
    }

    props.onSetupIntegrationService(newType);
  };

  return (
    <>
      {(type === AUTOMATION_ACTION_TYPE.SEND_EMAIL ||
        type === AUTOMATION_ACTION_TYPE.SEND_SMS ||
        type === AUTOMATION_ACTION_TYPE.SEND_MAIL ||
        type === AUTOMATION_ACTION_TYPE.SYNC_CONTACT ||
        type === AUTOMATION_ACTION_TYPE.SYNC_REMOVE_CONTACT ||
        type === AUTOMATION_ACTION_TYPE.SEND_RINGLESS_VM) && (
        <>
          <div className="mb-3 col-4">
            <label className="label">Select Service</label>
            {userActionServicesLoading === true && (
              <div className="alert small alert-info" role="alert">
                <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
              </div>
            )}
            {userActionServicesLoading === false && userActionServices.length === 0 && (
              <div className="alert small alert-info" role="alert">
                No services available
                <br></br>
                <div
                  className="badge badge-pill badge-info cp"
                  style={{ fontSize: '8pt' }}
                  onClick={() => handleSetupIntegration(type)}
                >
                  <i className="far fa-plus-circle"></i> Add an integration
                </div>
                {/*
                                    <b>Integrate a service
                                        {
                                            type === AUTOMATION_ACTION_TYPE.SEND_SMS &&
                                            <a href="/agent/settings/voip"> here</a>
                                        }
                                        {
                                            type === AUTOMATION_ACTION_TYPE.SEND_MAIL &&
                                            <a href="/agent/settings/integration"> here</a>
                                        }
                                        {
                                            type === AUTOMATION_ACTION_TYPE.SYNC_CONTACT &&
                                            <a href="/agent/settings/integration"> here</a>
                                        }
                                        {
                                            type === AUTOMATION_ACTION_TYPE.SYNC_REMOVE_CONTACT &&
                                            <a href="/agent/settings/integration"> here</a>
                                        }
                                    </b>
                                    */}
              </div>
            )}
            {userActionServicesLoading === false && userActionServices.length > 0 && (
              <div className="input-group input-group-sm">
                <select
                  name="userIntegrationProviderId"
                  onChange={userServiceChange}
                  className="form-control form-control-sm"
                >
                  {
                    // Since mail and email have a default setting, we don't need the empty option
                    type !== AUTOMATION_ACTION_TYPE.SEND_EMAIL && <option></option>
                  }
                  {
                    // TODO: Sort this ID, make array of vendors that cant by synced instead of ternary below
                    userActionServices.map((item) => {
                      const selected =
                        (item.entityType &&
                          item.entityType === 'oauth' &&
                          userIntegrationProviderId &&
                          userIntegrationProviderId === item.value) ||
                        (item.entityType &&
                          item.entityType === 'pat' &&
                          appId &&
                          appId === item.value);
                      return item.label !== 'Aetna' ? (
                        <option selected={selected} value={item.reference}>
                          {item.label}
                        </option>
                      ) : (
                        ''
                      );
                    })
                  }
                </select>
              </div>
            )}
            {userActionServices.length > 0 && (
              <small>
                <i className="far fa-question-circle mr-1 "></i>What service do you want to use?
              </small>
            )}
          </div>

          {currentUserService && type === AUTOMATION_ACTION_TYPE.SEND_SMS && (
            <>
              {props.phoneNumbers && (
                <div className="mb-3 col-4">
                  <label className="label">Select Phone Number</label>
                  {userVoIPPhoneNumbersLoading === true && (
                    <div className="alert small alert-info" role="alert">
                      <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
                    </div>
                  )}
                  {userVoIPPhoneNumbersLoading === false && (
                    <div className="input-group input-group-sm">
                      <select
                        name="userVoIPPhoneNumberId"
                        onChange={userVoIPPhoneNumberChange}
                        className="form-control form-control-sm"
                      >
                        <option value={''}></option>
                        {userVoIPPhoneNumbers.length > 0 &&
                          userVoIPPhoneNumbers.map((item) => (
                            <option
                              selected={item.value === userVoIPPhoneNumberId}
                              value={item.value}
                            >
                              {item.label}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}
                  <small>
                    <i className="far fa-question-circle mr-1 "></i>If applicable, which phone
                    number would you like to use?
                  </small>
                </div>
              )}

              {props.extensions && (
                <div className="mb-3 col-4">
                  <label className="label">Select Extension</label>
                  {userVoIPExtensionsLoading === true && (
                    <div className="alert small alert-info" role="alert">
                      <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
                    </div>
                  )}
                  {userVoIPExtensionsLoading === false && (
                    <div className="input-group input-group-sm">
                      <select
                        name="userVoIPExtensionId"
                        onChange={userVoIPExtensionChange}
                        className="form-control form-control-sm"
                      >
                        <option value={''}></option>
                        {userVoIPExtensions.length > 0 &&
                          userVoIPExtensions.map((item) => (
                            <option
                              selected={item.value === userVoIPExtensionId}
                              value={item.value}
                            >
                              {item.label}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}
                  <small>
                    <i className="far fa-question-circle mr-1 "></i>If applicable, which extension
                    would you like to use?
                  </small>
                </div>
              )}
            </>
          )}

          {currentUserService && type === AUTOMATION_ACTION_TYPE.SEND_RINGLESS_VM && (
            <>
              {props.phoneNumbers && (
                <div className="mb-3 col-4">
                  <label className="label">Select audio file</label>
                  {userAudioFilesListsLoading === true && (
                    <div className="alert small alert-info" role="alert">
                      <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
                    </div>
                  )}
                  {userAudioFilesListsLoading === false && (
                    <div className="input-group input-group-sm">
                      <select
                        name="userAudioFileListId"
                        onChange={userAudioFileChange}
                        className="form-control form-control-sm"
                      >
                        <option value={''}></option>
                        {userAudioFilesLists.length > 0 &&
                          userAudioFilesLists.map((item) => (
                            <option selected={item.value === userAudioFileId} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}
                  <small>
                    <i className="far fa-question-circle mr-1 "></i>If applicable, which audio file
                    would you like to use?
                  </small>
                </div>
              )}
            </>
          )}

          {currentUserService &&
            (type === AUTOMATION_ACTION_TYPE.SYNC_CONTACT ||
              type === AUTOMATION_ACTION_TYPE.SYNC_REMOVE_CONTACT) && (
              <>
                {userContactSyncLists &&
                  servicesUsingListIdAsContactSyncList.includes(
                    userIntegrationProviderReference
                  ) && (
                    <div className="mb-3 col-8">
                      <label className="label">
                        {type === AUTOMATION_ACTION_TYPE.SYNC_CONTACT && (
                          <>Select List/Group to sync to</>
                        )}
                        {type === AUTOMATION_ACTION_TYPE.SYNC_REMOVE_CONTACT && (
                          <>Select List/Group to remove from</>
                        )}
                      </label>
                      {userContactSyncListsLoading === true && (
                        <div className="alert small alert-info" role="alert">
                          <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
                        </div>
                      )}
                      {userContactSyncListsLoading === false && (
                        <div className="input-group input-group-sm">
                          <select
                            name="userContactSyncListId"
                            onChange={userContactSyncListIdChange}
                            className="form-control form-control-sm"
                          >
                            <option></option>
                            {userContactSyncLists.length > 0 &&
                              userContactSyncLists.map((item) => (
                                <option
                                  selected={item.value == userContactSyncListId}
                                  value={item.value}
                                >
                                  {item.label}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}

                      <small>
                        <i className="far fa-question-circle mr-1 "></i>
                        {type === AUTOMATION_ACTION_TYPE.SYNC_CONTACT && (
                          <>If applicable, which list/group do you want to sync the contact to?</>
                        )}
                        {type === AUTOMATION_ACTION_TYPE.SYNC_REMOVE_CONTACT && (
                          <>
                            If applicable, which list/group do you want to remove the contact from?
                          </>
                        )}
                      </small>
                    </div>
                  )}

                {userContactSyncLists &&
                  servicesUsingExtensionIdAsContactSyncList.includes(
                    userIntegrationProviderReference
                  ) && (
                    <>
                      <div className="mb-3 col-4">
                        <label className="label">
                          {type === AUTOMATION_ACTION_TYPE.SYNC_CONTACT && (
                            <>Select extension to sync to</>
                          )}
                          {type === AUTOMATION_ACTION_TYPE.SYNC_REMOVE_CONTACT && (
                            <>Select extension to remove from</>
                          )}
                        </label>
                        {userContactSyncListsLoading === true && (
                          <div className="alert small alert-info" role="alert">
                            <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
                          </div>
                        )}
                        {userContactSyncListsLoading === false && (
                          <div className="input-group input-group-sm">
                            <select
                              name="userContactSyncListId"
                              onChange={userContactSyncExtensionIdChange}
                              className="form-control form-control-sm"
                            >
                              <option></option>
                              {userContactSyncLists.length > 0 &&
                                userContactSyncLists.map((item) => (
                                  <option
                                    selected={item.value === userContactSyncExtensionId}
                                    value={item.value}
                                  >
                                    {item.label}
                                  </option>
                                ))}
                            </select>
                          </div>
                        )}

                        <small>
                          <i className="far fa-question-circle mr-1 "></i>
                          {type === AUTOMATION_ACTION_TYPE.SYNC_CONTACT && (
                            <>If applicable, which extension do you want to sync the contact to?</>
                          )}
                          {type === AUTOMATION_ACTION_TYPE.SYNC_REMOVE_CONTACT && (
                            <>
                              If applicable, which extension do you want to remove the contact from?
                            </>
                          )}
                        </small>
                      </div>

                      <div className="mb-3 col-4">
                        <label className="label">
                          {type === AUTOMATION_ACTION_TYPE.SYNC_CONTACT && (
                            <>Select group to sync to</>
                          )}
                          {type === AUTOMATION_ACTION_TYPE.SYNC_REMOVE_CONTACT && (
                            <>Select group to remove from</>
                          )}
                        </label>
                        {userContactSyncGroupsLoading === true && (
                          <div className="alert small alert-info" role="alert">
                            <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
                          </div>
                        )}
                        {userContactSyncGroupsLoading === false && (
                          <div className="input-group input-group-sm">
                            <select
                              name="userContactSyncListId"
                              onChange={userContactSyncGroupIdChange}
                              className="form-control form-control-sm"
                            >
                              <option></option>
                              {userContactSyncGroupLists.length > 0 &&
                                userContactSyncGroupLists.map((item) => (
                                  <option
                                    selected={item.groupId === userContactSyncGroupId}
                                    value={item.groupId}
                                  >
                                    {item.groupName}
                                  </option>
                                ))}
                            </select>
                          </div>
                        )}

                        <small>
                          <i className="far fa-question-circle mr-1 "></i>
                          {type === AUTOMATION_ACTION_TYPE.SYNC_CONTACT && (
                            <>If applicable, which group do you want the contact in?</>
                          )}
                          {type === AUTOMATION_ACTION_TYPE.SYNC_REMOVE_CONTACT && (
                            <>If applicable, which group do you want the contact removed from?</>
                          )}
                        </small>
                      </div>
                    </>
                  )}

                {userContactSyncLists &&
                  servicesUsingAccountIdAsContactSyncList.includes(
                    userIntegrationProviderReference
                  ) && (
                    <div className="mb-3 col-8">
                      <label className="label">Select List/Group to sync to</label>
                      {userContactSyncListsLoading === true && (
                        <div className="alert small alert-info" role="alert">
                          <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
                        </div>
                      )}
                      {userContactSyncListsLoading === false && (
                        <div className="input-group input-group-sm">
                          <select
                            name="userContactSyncListId"
                            onChange={userContactSyncAccountIdChange}
                            className="form-control form-control-sm"
                          >
                            <option></option>
                            {userContactSyncLists.length > 0 &&
                              userContactSyncLists.map((item) => (
                                <option
                                  selected={item.value == userContactSyncAccountId}
                                  value={item.value}
                                >
                                  {item.label}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}

                      <small>
                        <i className="far fa-question-circle mr-1 "></i>If applicable, which account
                        do you want to sync the contact to?
                      </small>
                    </div>
                  )}
              </>
            )}

          {props.templates &&
            type !== AUTOMATION_ACTION_TYPE.SYNC_CONTACT &&
            type !== AUTOMATION_ACTION_TYPE.SYNC_REMOVE_CONTACT &&
            type !== AUTOMATION_ACTION_TYPE.SEND_RINGLESS_VM && (
              <div className="mb-3 col-4">
                <label className="label">Select Template</label>

                {currentUserService && currentUserService.reference === 'thanksio' && (
                  <ThanksIOTemplateSelect
                    userId={props.userId}
                    userAccessToken={props.userAccessToken}
                    onSave={(thanksioTemplate) => {
                      setThanksioTemplate(thanksioTemplate);
                      if (props.onThanksioTemplateChange) {
                        props.onThanksioTemplateChange(thanksioTemplate);
                      }
                    }}
                    templateData={thanksioTemplate}
                  />
                )}
                {currentUserService && currentUserService.reference === 'directmailmanager' && (
                  <DirectMailManagerTemplateSelect
                    userId={props.userId}
                    userAccessToken={props.userAccessToken}
                    onSave={(directMailManagerTemplate) => {
                      setDirectMailManagerTemplate(directMailManagerTemplate);
                      if (props.onDirectMailManagerTemplateChange) {
                        props.onDirectMailManagerTemplateChange(directMailManagerTemplate);
                      }
                    }}
                    templateData={directMailManagerTemplate}
                  />
                )}
                {
                  // TODO: Improve this
                  (!currentUserService ||
                    (currentUserService &&
                      currentUserService.reference !== 'thanksio' &&
                      currentUserService.reference !== 'directmailmanager')) && (
                    <UserLetterTemplateSelect
                      userId={props.userId}
                      userAccessToken={props.userAccessToken}
                      type={type}
                      userLetterTemplateId={userLetterTemplateId}
                      onUserLetterTemplateIdChange={(userLetterTemplateId) => {
                        setUserLetterTemplateId(userLetterTemplateId);
                        if (props.onUserLetterTemplateIdChange) {
                          props.onUserLetterTemplateIdChange(userLetterTemplateId);
                        }
                      }}
                      onSetupTemplate={props.onSetupTemplate}
                      refreshKey={props.refreshKey}
                    />
                  )
                }
              </div>
            )}
        </>
      )}
    </>
  );
};
